import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import sift from 'sift';

import { DATA_TYPES, FILTER_LOGIC, SLIDER_OPERATORS } from '../components/Filter/constants';
import { filterProducts } from '../redux/slices/categoryDataSlice';
import { Ranking } from '../utils/ranking/ranking';
import { CIDS } from '../components/ProductsTable/constants';
import { updatePartCountForFilters } from '../redux/slices/filtersSlice';

export const useFilters = (categoryId, displayModeIsWPN) => {
	const columns = useSelector(state => state.categoryData.columns);
	const filters = useSelector(state => state.categoryData.filters);
	const filterRelations = useSelector(state => state.categoryData.filterRelations);
	const products = useSelector(state => state.categoryData.products);
	const filtersState = useSelector(state => state.filters.appliedState);
	const chartFilterState = useSelector(state => state.chartFilters.filtrationParameters);
	const similarParts = useSelector(state => state.filters.similarParts.parts);
	const dispatch = useDispatch();

	useEffect(() => {
		// process filterRelations and split filters to AND/OR logic groups
		const ANDParameters = Object.keys(filtersState);
		const ORParameters = [];

		try {
			for (const relation of filterRelations) {
				let conditionsMet = true;

				for (const condition of relation.conditions) {
					const appliedValue = filtersState[condition.cid].value;
					const arrayValuesAreNotEqual =
						Array.isArray(appliedValue) && JSON.stringify(appliedValue) !== JSON.stringify(condition.values);
					const singleValuesAreNotEqual = appliedValue !== condition.values[0];

					if (arrayValuesAreNotEqual || singleValuesAreNotEqual) {
						conditionsMet = false;
						break;
					}
				}

				if (conditionsMet) {
					ORParameters.push(relation.cids);

					for (const cid of relation.cids) {
						const index = ANDParameters.indexOf(cid);
						if (index > -1) {
							ANDParameters.splice(index, 1);
						}
					}
				}
			}
		} catch (e) {
			console.error('Error creating logic groups:', e);
		}

		// loop AND/OR logic groups and build siftInput
		let siftInput = [];

		try {
			for (const parameter of ANDParameters) {
				const filter = filters.find(f => f.cid === parameter);
				const siftParameter = processFilterState(filter, filtersState[parameter], FILTER_LOGIC.AND);
				if (siftParameter !== null && !Array.isArray(siftParameter)) {
					siftInput.push(siftParameter);
				} else if (Array.isArray(siftParameter)) {
					siftInput = siftInput.concat(siftParameter);
				}
			}

			for (const parameterGroup of ORParameters) {
				let siftParameterGroup = [];

				for (const parameter of parameterGroup) {
					const filter = filters.find(f => f.cid === parameter);
					const siftParameter = processFilterState(filter, filtersState[parameter], FILTER_LOGIC.OR);

					if (siftParameter !== null && !Array.isArray(siftParameter)) {
						siftParameterGroup.push(siftParameter);
					} else if (Array.isArray(siftParameter)) {
						siftParameterGroup = siftParameterGroup.concat(siftParameter);
					}
				}

				if (siftParameterGroup.length > 0) {
					siftInput.push({
						$or: siftParameterGroup.map(siftParam => siftParam)
					});
				}
			}
		} catch (e) {
			console.error('Error building siftInput:', e);
		}

		const siftChartInput = buildSiftChartInput(chartFilterState.cid, chartFilterState.value);

		let newFilteredProducts;
		if (chartFilterState.value !== null) {
			newFilteredProducts = products.filter(sift({ $and: siftChartInput }));
			newFilteredProducts = newFilteredProducts.filter(sift({ $and: siftInput }));
		} else {
			newFilteredProducts = products.filter(sift({ $and: siftInput }));
		}

		const partCountForFilters = preparePartCountForFilters(newFilteredProducts, siftInput);
		dispatch(updatePartCountForFilters(partCountForFilters));

		const ranking = new Ranking(newFilteredProducts, categoryId, filtersState, findUsedFilters(), displayModeIsWPN);
		const reRankedProducts = ranking.update(similarParts);

		dispatch(filterProducts(reRankedProducts));

		function buildSiftChartInput(cid, value) {
			if (value !== null) {
				return [
					{
						[cid]: {
							$eq: value
						}
					}
				];
			}
		}

		function buildSiftParameter(dataType, cid, constraints, value, groupLogic) {
			if (cid === CIDS.V_OUT_APLICATION_ORIENTED) {
				return addVoutPowerSupplyCondition(value);
			}
			// single string/number value - select menu, chips, input, etc. (not multiSelect)
			if (
				(dataType === DATA_TYPES.NUMERIC && !constraints?.minOperator && !constraints?.maxOperator) ||
				dataType === DATA_TYPES.STRING
			) {
				if (Array.isArray(value)) {
					const logicOperator = filtersState[cid].logic === FILTER_LOGIC.NOT ? '$nin' : '$in';

					return {
						[cid]: { [logicOperator]: value }
					};
				} else {
					return { [cid]: value };
				}
			}
			// numeric ranges with constraints - single/range sliders
			else if (dataType === DATA_TYPES.NUMERIC && (constraints?.minOperator || constraints?.maxOperator)) {
				const siftConstraints = {};
				const minOperator = constraints && constraints?.minOperator;
				const maxOperator = constraints && constraints?.maxOperator;

				try {
					if (minOperator && maxOperator) {
						siftConstraints['$gte'] = value[0];
						siftConstraints['$lte'] = value[1];
					} else {
						if (minOperator) {
							siftConstraints[SLIDER_OPERATORS[minOperator].siftOperator] = value;
						} else {
							siftConstraints[SLIDER_OPERATORS[maxOperator].siftOperator] = value;
						}
					}
				} catch (e) {
					console.error('Error building numeric sift parameter:', e);
				}

				return groupLogic === FILTER_LOGIC.OR
					? { [cid]: siftConstraints }
					: {
							[cid]: {
								$or: [siftConstraints, { $eq: null }]
							}
					  };
			}
			// comparison of boolean values (checkboxes)
			else if (dataType === DATA_TYPES.BOOLEAN) {
				if (Array.isArray(value)) {
					return { [cid]: { $in: value } };
				} else {
					return { [cid]: value };
				}
			} else {
				return '';
			}
		}

		function addVoutPowerSupplyCondition(value) {
			return [
				{
					von: {
						$or: [
							{
								$lte: value
							},
							{
								$eq: null
							}
						]
					}
				},
				{
					vox: {
						$or: [
							{
								$gte: value
							},
							{
								$eq: null
							}
						]
					}
				}
			];
		}

		function processFilterState(filter, state, groupLogic) {
			if (
				!state.disabled &&
				state.value !== null &&
				state.value !== '' &&
				(!Array.isArray(state.value) || state.value.length > 0)
			) {
				if (filter.conditional) {
					// if a conditional filter has 'All' value selected, it is excluded from sift parameters
					if (state.value === 'All') {
						return null;
					}

					const siftParameters = [];
					const arrayValue = Array.isArray(state.value) ? state.value : [state.value];

					arrayValue.forEach(reduxVal => {
						const conditions = filter.values.find(val => val.label === reduxVal).conditions;

						conditions.forEach(condition => {
							const dataType = columns.find(column => column.cid === condition.cid).type;
							const builtSiftParameter = buildSiftParameter(
								dataType,
								condition.cid,
								filter.constraints,
								condition.values,
								groupLogic
							);
							if (Array.isArray(builtSiftParameter)) {
								siftParameters.concat(builtSiftParameter);
							} else {
								siftParameters.push(builtSiftParameter);
							}
						});
					});

					if (filter.multiSelect && state.logic === FILTER_LOGIC.OR) {
						return {
							$or: siftParameters.map(param => param)
						};
					} else if ((!filter.multiSelect && !state.logic) || state.logic === FILTER_LOGIC.AND) {
						return {
							$and: siftParameters.map(param => param)
						};
					} else if (state.logic === FILTER_LOGIC.NOT) {
						return {
							$not: {
								$and: siftParameters.map(param => param)
							}
						};
					} else {
						throw new Error('Incorrectly set "multiSelect" / "logic" attributes in filter cid: ' + filter.cid);
					}
				} else {
					return buildSiftParameter(filter.type, filter.cid, filter.constraints, state.value, groupLogic);
				}
			}

			return null;
		}

		function findUsedFilters() {
			const usedFilters = {};
			const usedFilterCids = Object.keys(filtersState).filter(cid => filtersState[cid].used === true);

			for (const cid of usedFilterCids) {
				usedFilters[cid] = filtersState[cid].value;
			}

			return usedFilters;
		}

		function preparePartCountForFilters(productFilteredByTable, siftInput) {
			// Initialize and reset counts for all filter options
			const countOfPartsForFilterOptions = {};
			const excludedFilters = [];
			filters.forEach(filter => {
				if (filter.partCountEnabled && (filter.type === 'S' || filter.type === 'B')) {
					const filterOptions = {};
					if (filter.conditional) {
						filter.values.forEach(value => {
							if (value.label !== 'All') {
								filterOptions[value.label] = {
									valuesCid: value.conditions[0].cid,
									values: value.conditions[0].values,
									count: 0
								};
							}
						});
					} else {
						filter.values.forEach(value => {
							filterOptions[value] = {
								valuesCid: filter.cid,
								values: [value],
								count: 0
							};
						});
					}
					countOfPartsForFilterOptions[filter.cid] = filterOptions;
				} else {
					excludedFilters.push(filter.cid);
				}
			});

			if (Object.keys(countOfPartsForFilterOptions).length > 0) {
				// Create default count based on filtered products in product table
				let mapOfFilterValuesCountForInactiveFilters = new Map();
				Object.keys(products[0]).forEach(key => {
					if (!excludedFilters.includes(key)) {
						mapOfFilterValuesCountForInactiveFilters.set(key, new Map());
					}
				});
				for (const part of Object.values(productFilteredByTable)) {
					for (const [key, value] of Object.entries(part)) {
						const mapOfValuesCount = mapOfFilterValuesCountForInactiveFilters.get(key);
						if (value !== null && mapOfValuesCount) {
							mapOfValuesCount.set(value, (mapOfValuesCount.get(value) || 0) + 1);
						}
					}
				}

				// Update counts for filter options
				for (const filterCid in countOfPartsForFilterOptions) {
					const filterOptions = countOfPartsForFilterOptions[filterCid];
					const filterIsUsed = filtersState[filterCid].used;
					const partCountForActiveFilter = filterIsUsed ? gainPartCountForFilter(filterCid, siftInput) : null;

					for (const filterOption in filterOptions) {
						const optionDescription = filterOptions[filterOption];
						const valuesCid = optionDescription.valuesCid;
						if (filterIsUsed) {
							// Create count based on filtered products specific for used filter
							const filterDescription = filters.filter(f => f.cid === filterCid)[0];
							if (filterDescription.type === 'B' && filterDescription.conditional) {
								const partCountForActiveConditionalBooleanFilter = gainPartCountForFilter(valuesCid, siftInput);
								partCountForActiveConditionalBooleanFilter.forEach((value, key) => {
									if (optionDescription.values.includes(key)) {
										optionDescription.count += value;
									}
								});
							} else {
								partCountForActiveFilter.forEach((value, key) => {
									if (optionDescription.values.includes(key)) {
										optionDescription.count += value;
									}
								});
							}
						} else {
							mapOfFilterValuesCountForInactiveFilters.get(valuesCid).forEach((value, key) => {
								if (optionDescription.values.includes(key)) {
									optionDescription.count += value;
								}
							});
						}
					}
				}
			}

			return countOfPartsForFilterOptions;
		}

		function gainPartCountForFilter(filterCid, siftInput) {
			const newSiftInput = siftInput.filter(item => !JSON.stringify(item).includes(`"${filterCid}":{`) && item);
			const filteredPartsWithoutActualFilterApplied = products.filter(sift({ $and: newSiftInput }));
			const mapOfValuesCount = new Map();

			// Update counts based on filtered products
			for (const part of Object.values(filteredPartsWithoutActualFilterApplied)) {
				const value = part[filterCid];
				if (value !== null) {
					mapOfValuesCount.set(value, (mapOfValuesCount.get(value) || 0) + 1);
				}
			}
			return mapOfValuesCount;
		}
	}, [
		categoryId,
		columns,
		filters,
		filterRelations,
		products,
		filtersState,
		chartFilterState,
		displayModeIsWPN,
		similarParts,
		dispatch
	]);
};
